import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SocialPostDetailDto } from "@multi-chat/shared";
import {
  ChevronDown as ChevronDownIcon,
  ExternalLink as ExternalLinkIcon,
  Image as ImageIcon,
} from "lucide-react";
import { ChannelIdentityBadge } from "../../../components/channel/ChannelIdentityBadge.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { cn } from "../../../lib/utils.js";
import { formatConversationFullDate } from "../conversation-time.js";
import { resolveConnectedAccountLabel } from "./comment-identity.js";

/**
 * Dominant original-post context (PostHero) for the Post Comments center pane.
 */
export function SocialPostContextCard({
  post,
  compact = false,
  defaultExpanded = true,
}: {
  post: SocialPostDetailDto;
  /** Sticky compact bar while scrolling thread on mobile. */
  compact?: boolean;
  defaultExpanded?: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded && !compact);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const published = post.publishedAt
    ? formatConversationFullDate(post.publishedAt, i18n.language)
    : null;
  const caption = post.caption?.trim() || t("inbox.comments.post.noCaption");
  const longCaption = (post.caption?.trim().length ?? 0) > 160;
  const account = resolveConnectedAccountLabel({
    accountDisplayName: post.accountDisplayName,
    accountUsername: post.accountUsername,
    fallbackLabel: post.endpointLocalName?.trim() || t("inbox.comments.post.accountFallback"),
  });

  if (compact) {
    return (
      <section className="flex shrink-0 items-center gap-2.5 border-b border-line/70 bg-surface px-3 py-2">
        <PostThumb url={post.mediaPreviewUrl} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <ChannelIdentityBadge platformType={post.platformType} size="inline" />
            <p className="m-0 min-w-0 truncate text-[0.78125rem] font-semibold text-ink" dir="auto">
              {caption}
            </p>
          </div>
          <p className="m-0 mt-0.5 text-[0.6875rem] text-muted">
            {t("inbox.comments.post.openCount", {
              open: post.openCommentsCount,
              total: post.totalCommentsCount,
            })}
          </p>
        </div>
        {post.permalinkUrl ? (
          <a
            href={post.permalinkUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-grid size-8 shrink-0 place-items-center rounded-lg text-primary hover:bg-brand-mint focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
            aria-label={t("inbox.comments.post.openExternal")}
          >
            <ExternalLinkIcon size={14} aria-hidden="true" />
          </a>
        ) : null}
      </section>
    );
  }

  return (
    <section className="shrink-0 border-b border-line/70 bg-surface">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3.5 py-2.5 text-start focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        <ChannelIdentityBadge platformType={post.platformType} size="inline" />
        <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-semibold text-ink">
          {t("inbox.comments.post.contextTitle")}
        </span>
        <span className="shrink-0 text-[0.6875rem] text-muted">
          {t("inbox.comments.post.openCount", {
            open: post.openCommentsCount,
            total: post.totalCommentsCount,
          })}
        </span>
        <ChevronDownIcon
          size={16}
          className={cn(
            "shrink-0 text-muted transition-transform duration-150",
            !expanded && "-rotate-90 rtl:rotate-90",
          )}
          aria-hidden="true"
        />
      </button>

      {expanded ? (
        <div className="flex gap-3.5 px-3.5 pb-3.5">
          <PostThumb url={post.mediaPreviewUrl} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="m-0 mb-0.5 text-[0.8125rem] font-semibold text-ink" dir="auto">
              {account.primary}
            </p>
            {account.secondaryUsername ? (
              <p className="m-0 mb-1.5 text-[0.71875rem] text-muted">
                <LtrValue>@{account.secondaryUsername}</LtrValue>
              </p>
            ) : null}
            <p
              className={cn(
                "m-0 text-[0.875rem] leading-snug text-ink",
                !captionExpanded && longCaption && "line-clamp-3",
              )}
              dir="auto"
            >
              {caption}
            </p>
            {longCaption ? (
              <button
                type="button"
                className="mt-1 text-[0.75rem] font-semibold text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                onClick={() => setCaptionExpanded((value) => !value)}
              >
                {captionExpanded
                  ? t("inbox.comments.post.captionCollapse")
                  : t("inbox.comments.post.captionExpand")}
              </button>
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[0.71875rem] text-muted">
              {published ? (
                <time dateTime={post.publishedAt ?? undefined}>
                  <LtrValue>{published}</LtrValue>
                </time>
              ) : null}
              {post.permalinkUrl ? (
                <a
                  href={post.permalinkUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  {t("inbox.comments.post.openExternal")}
                  <ExternalLinkIcon size={12} aria-hidden="true" />
                </a>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function PostThumb({
  url,
  size,
}: {
  url: string | null;
  size: "sm" | "lg";
}): React.JSX.Element {
  return (
    <div
      className={cn(
        "shrink-0 overflow-hidden rounded-xl bg-surface-muted",
        size === "lg" ? "size-32 sm:size-36" : "size-11",
      )}
    >
      {url ? (
        <img src={url} alt="" className="size-full object-cover" draggable={false} />
      ) : (
        <span className="grid size-full place-items-center text-muted">
          <ImageIcon size={size === "lg" ? 28 : 18} aria-hidden="true" />
        </span>
      )}
    </div>
  );
}
